/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import enjoyFreeImage from './enjoy-free.png'
import { trackVideoFreeAccessModalAction } from 'src/utils/videoAnalytics'

interface FreeVideoAccessModalProps {
  isOpen: boolean
  onClose: () => void
  game?: {
    id?: string
    sport_id?: string | number
    generic_sport_id?: string | number | null
    feature_flags?: Record<string, boolean>
  } | null
}

/**
 * Modal to inform users about free video access during beta
 */
const FreeVideoAccessModal = ({
  isOpen,
  onClose,
  game = null,
}: FreeVideoAccessModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const onPressLearnMore = useCallback(() => {
    trackVideoFreeAccessModalAction(game, { action: 'learn_more' })
    window.open('https://support.sportninja.com/faq', '_blank')
  }, [game])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
        .ant-modal-close {
          top: 24px;
          right: 24px;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      maskClosable={true}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 16px;
          position: relative;
          padding-top: 16px;
        `}
      >
        {/* Image */}
        <img
          src={enjoyFreeImage}
          alt='Enjoy Free Video Access'
          css={css`
            width: 128px;
            height: 128px;
            object-fit: contain;
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 28px;
            font-weight: 600;
            line-height: 32px;
            text-align: center;
          `}
        >
          Enjoy Free Video Access
        </Text>

        {/* Subtitle */}
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_50}
          style='text-align: center; max-width: 360px;'
        >
          During The Beta Phase, All Video Content Is Completely Free.
          Subscriptions Are Coming Soon!
        </Text>

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
            margin-top: 8px;
          `}
        />

        {/* Continue Watching button - right aligned */}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            width: 100%;
            margin-top: 8px;
            gap: 16px;
          `}
        >
          <Button
            text='LEARN MORE'
            onClick={onPressLearnMore}
            variant='secondary'
            uppercase
            style={css`
              flex: 1 0 auto;
            `}
          />
          <Button
            text='CONTINUE WATCHING'
            onClick={() => {
              trackVideoFreeAccessModalAction(game, {
                action: 'continue_watching',
              })
              onClose()
            }}
            variant='primary'
            uppercase
            style={css`
              flex: 1 0 auto;
            `}
          />
        </div>
      </div>
    </Modal>
  )
}

export default FreeVideoAccessModal
